<template>
    <ui-modal>
        <template #header>
            <h3
                v-text="
                    `${isEditMode ? 'Edycja' : 'Rejestracja'}
                    umowy z Operatorem ${selectedOperator.name}`"
                class="text-center mb-1"
            />
        </template>

        <h5 v-text="'Dane umowy'" class="font-weight-medium mb-3" />
        <div class="operator-contract__dates">
            <date-input
                required
                placeholder="Data zawarcia umowy"
                class="operator-contract__date"
                :val="contract.startDate"
                :id="`startDate-${selectedOperator.id}`"
                :lock-future-days="[contract.endDate]"
                @change="contract.startDate = $event"
            />
            <date-input
                required
                placeholder="Data zakończenia umowy"
                class="operator-contract__date"
                :val="contract.endDate"
                :id="`endDate-${selectedOperator.id}`"
                :disabled="contract.indefinite"
                :lock-past-days="[$moment().format('YYYY-MM-DD'), contract.startDate]"
                @change="contract.endDate = $event"
            />
            <div class="d-flex align-items-center">
                <ui-checkbox-input
                    v-model="contract.indefinite"
                    name="indefinite"
                    class="text-primary mb-0"
                    label="Umowa na czas nieokreślony"
                    @change="val => { if (val && contract.endDate) contract.endDate = '' }"
                />
            </div>
        </div>

        <h5 v-text="'Frakcje objęte umową'" class="font-weight-medium mb-3 mt-4" />
        <div class="operator-contract__fractions">
            <fraction-switch
                v-for="fraction in contract.fractions"
                show-details
                class="operator-contract__fraction"
                :class="{ active: fraction.enabled }"
                :key="fraction.id"
                :fraction="fraction"
                @change="toggleFraction($event)"
            />
        </div>

        <h5 v-text="'Domyślne stawki za obsługę frakcji'" class="font-weight-medium mb-3 mt-4" />
        <div class="operator-contract__fees">
            <div class="operator-contract__fee">
                <h6>
                    Zbiórka manualna
                </h6>
                <money-input
                    class="m-0"
                    style="width: 7rem"
                    :price="contract.defaultHandlingFee"
                    :decimal-places="3"
                    @onChange="contract.defaultHandlingFee = $event"
                />
            </div>
            <div class="operator-contract__fee">
                <h6>
                    Zbiórka automatyczna
                    <a-icon
                        icon="Info"
                        width="16"
                        height="16"
                        v-tooltip="'Jeśli nie prowadzisz zbiórki automatycznej w sklepie, wpisz 0'"
                    />
                </h6>
                <money-input
                    class="m-0"
                    style="width: 7rem"
                    :price="contract.defaultHandlingFeeRvm"
                    :decimal-places="3"
                    :init-validation="false"
                    @onChange="contract.defaultHandlingFeeRvm = $event"
                />
            </div>
        </div>

        <template #footer>
            <the-btn
                class="btn-outline-custom"
                text="Wróć"
                :disabled="isCreateInProgress"
                @click="goToSelectOperator"
            />
            <the-btn
                v-tooltip="tooltipText"
                class="btn-secondary"
                :text="isEditMode
                    ? 'Zapisz zmiany i przejdź do edycji sklepów'
                    : 'Zapisz umowę'"
                :loading-status="contractStatus"
                :disabled="isCreateInProgress || !isContractValid"
                @click="sendContractAndGoToSummary"
            />
        </template>
    </ui-modal>
</template>

<script>
import DrsOperatorsStore from '@/store/modules/drs/drs-operators.module'
import { registerPageModule } from '@/utils/store'
import { mapGetters, mapActions } from 'vuex'
import { OPERATOR_CODES } from '@/utils/constants/drs/operators'
import { CONTRACT_MODES } from './-utils'
import cloneDumb from '@/helpers/cloneDumb'

export default {
    data() {
        return {
            contractStatus: 'idle',
            isLoading: false,
            expandedIdx: null,
            contractsData: {},
            locations: [],
            contract: {
                startDate: null,
                endDate: null,
                indefinite: false,
                fractions: [],
                defaultHandlingFee: 0,
                defaultHandlingFeeRvm: 0
            }
        }
    },
    beforeCreate() {
        registerPageModule(this.$store, DrsOperatorsStore, 'drsOperators')
    },
    created() {
        this.loadAndInitLocations()

        this.contract.fractions = this.fractions.map(fr => ({
            ...fr,
            enabled: false,
            handlingFee: 0
        }))

        if (this.isEditMode) {
            const { id, startDate, endDate, fractions, defaultHandlingFee, defaultHandlingFeeRvm } =
                this.existingContract

            this.contract.id = id
            if (startDate) {
                this.contract.startDate = this.$moment(startDate).format('YYYY-MM-DD')
            }
            if (endDate) {
                setTimeout(() => {
                    this.contract.endDate = this.$moment(endDate).format('YYYY-MM-DD')
                }, 0)
            } else {
                this.contract.indefinite = true
            }
            this.contract.defaultHandlingFee = defaultHandlingFee / 100
            this.contract.defaultHandlingFeeRvm = defaultHandlingFeeRvm / 100

            this.contract.fractions.forEach(fr => {
                const contractFraction = fractions.find(f => f === fr.id)
                if (contractFraction) {
                    fr.enabled = true
                    fr.handlingFee = contractFraction.handlingFee / 100
                }
            })
        }
    },
    computed: {
        ...mapGetters('drsOperators', {
            createStatus: 'getCreateContractsStatus',
            operatorSubs: 'getOperatorSubs',
            fractions: 'getFractions',
            existingContract: 'operatorContract'
        }),
        ...mapGetters('drs', ['shopsWithActiveService']),

        modalArgs() {
            return this.$store.getters.modalState.args
        },

        selectedOperator() {
            return this.modalArgs.selectedOperator
        },

        isEditMode() {
            return this.modalArgs.mode === CONTRACT_MODES.EDIT
        },

        isContractValid() {
            const { startDate, endDate, indefinite, fractions, defaultHandlingFee } = this.contract
            const hasDates = startDate && (endDate || indefinite)
            const hasEnabledFraction = fractions.some(fraction => fraction.enabled)
            const hasDefaultHandlingFee = +defaultHandlingFee > 0

            return hasDates && hasEnabledFraction && hasDefaultHandlingFee
        },

        tooltipText() {
            return !this.isContractValid
                ? 'Aby zapisać umowę, uzupełnij daty, wybierz przynajmniej jedną frakcję i podaj stawkę dla zbiórki manualnej.'
                : null
        },

        isCreateInProgress() {
            return this.contractStatus === 'loading'
        }
    },
    methods: {
        ...mapActions('drs', ['fetchLocationsWithCompanyId']),
        ...mapActions('drsOperators', ['createContract', 'updateContract', 'loadAndMapContract']),

        async loadAndInitLocations() {
            this.isLoading = true
            let drsLocations = this.shopsWithActiveService

            // load drs locations if not loaded, then set this.locations
            if (!drsLocations.length) {
                drsLocations = await this.fetchLocationsWithCompanyId()
            }

            // subscriptions for the selected operator
            const operatorSubs = this.operatorSubs.find(
                ({ id }) => id === this.selectedOperator.id
            )?.subscriptions || []

            // filter locations without a contract
            this.locations = drsLocations
                .filter(({ id }) => !operatorSubs.length || !operatorSubs.includes(id))
                .map(loc => ({ ...loc, checked: true }))

            this.isLoading = false
        },

        goToSelectOperator() {
            this.$store.dispatch('toggleModal', false)
            this.$nextTick(() => {
                this.$store.dispatch('openModal', {
                    componentPath: 'drs/operators/SelectOperator',
                    args: {
                        skipFetchSubs: true
                    }
                })
            })
        },
        async sendContractAndGoToSummary() {
            if (this.contractStatus === 'loading') {
                return
            }

            this.contractStatus = 'loading'

            // createContracts
            const {
                startDate,
                endDate,
                indefinite,
                fractions,
                defaultHandlingFee,
                defaultHandlingFeeRvm
            } = cloneDumb(this.contract)
            const companyId = this.locations[0]?.companyId || null

            if (this.isEditMode) {
                const contractFractions = this.contract.fractions.filter(f => f.enabled).map(f => f.id)
                const existingFractions = this.existingContract.fractions || []

                // Check if fractions are the same
                const fractionsChanged =
                    contractFractions.length !== existingFractions.length ||
                    !contractFractions.every(f => existingFractions.includes(f))

                // Check if the contract has changed before sending the update request
                const hasChanges =
                    startDate !== this.$moment(this.existingContract.startDate).format('YYYY-MM-DD') ||
                    endDate !== (this.existingContract.endDate ? this.$moment(this.existingContract.endDate).format('YYYY-MM-DD') : null) ||
                    parseFloat(defaultHandlingFee) !== this.existingContract.defaultHandlingFee / 100 ||
                    parseFloat(defaultHandlingFeeRvm) !== this.existingContract.defaultHandlingFeeRvm / 100 ||
                    fractionsChanged

                if (!hasChanges) {
                    // No changes detected, proceed to the next step without making an API call
                    this.$store.dispatch('openModal', {
                        componentPath: 'drs/operators/ContractLocations',
                        args: {
                            operator: this.selectedOperator,
                            contract: this.contract,
                            mode: CONTRACT_MODES.EDIT
                        }
                    })
                    this.contractStatus = 'idle'
                    return
                }

                const { data } = await this.updateContract({
                    contractId: this.existingContract.id,
                    payload: {
                        operatorServiceId: this.selectedOperator.id,
                        defaultHandlingFee: parseFloat(defaultHandlingFee) * 100,
                        defaultHandlingFeeRvm: parseFloat(defaultHandlingFeeRvm) * 100,
                        startDate,
                        endDate: indefinite ? null : endDate,
                        fractions: fractions.filter(f => f.enabled).map(f => f.id),
                        companyId
                    }
                })

                await this.loadAndMapContract(this.existingContract.operatorServiceId)

                this.$store.dispatch('openModal', {
                    componentPath: 'drs/operators/ContractLocations',
                    args: {
                        operator: this.selectedOperator,
                        contract: data,
                        mode: CONTRACT_MODES.EDIT,
                        hasChanges: true
                    }
                })

                return
            }

            try {
                const { data, status } = await this.createContract({
                    operatorServiceId: this.selectedOperator.id,
                    defaultHandlingFee: parseFloat(defaultHandlingFee) * 100,
                    defaultHandlingFeeRvm: parseFloat(defaultHandlingFeeRvm) * 100,
                    startDate,
                    endDate: indefinite ? null : endDate,
                    fractions: fractions.filter(f => f.enabled).map(f => f.id),
                    companyId
                })

                if (data.operatorServiceId) {
                    await this.loadAndMapContract(data.operatorServiceId)
                }

                this.$store.dispatch('openModal', {
                    componentPath: 'drs/operators/CreateContractSummary',
                    args: {
                        isSuccess: status === 201,
                        operator: this.selectedOperator,
                        contract: data
                    }
                })

                // goToSummary
                if (this.createStatus === 'ok' || this.createStatus === 'error') {
                    this.$store.dispatch('toggleModal', false)
                    this.$nextTick(() => {
                        this.goToSummary(this.createStatus)
                    })
                }
            } catch (error) {
                console.error('Error creating contract:', error)
                this.$store.dispatch('addNotification', {
                    type: 'danger',
                    content: 'Wystąpił błąd'
                })
            } finally {
                this.contractStatus = 'idle'
            }
        },
        goToSummary(status) {
            const isError = status === 'error'

            const details = isError
                ? `W trakcie rejestracji umowy wystąpił błąd.
                    Spróbuj ponownie lub wyślij zgłoszenie na
                    <a class="text-primary" href="mailto:pomoc@kaucyjni.pl">pomoc@kaucyjni.pl</a>.`
                : `Rejestracja umowy z Operatorem przebiegła pomyślnie.
                    Możesz teraz dodać kolejną umowę lub przejść do panelu zarządzania
                    Systemem Kaucyjnym. Rejestracja umów jest również możliwa z poziomu
                    zakładki System Kaucyjny w sekcji Operatorzy i umowy. Nie zapomnij skonfigurować
                    zbiórki dla swoich sklepów - opcja dostępna jest w sekcji Sklepy.`

            const rejectBtn = {
                text: 'Przejdź do panelu System Kaucyjny',
                onRejectBtn: () => {
                    this.$router.push({ name: 'drs' })
                    this.$store.dispatch('toggleModal', false)
                }
            }

            const acceptBtn = {
                text: isError ? 'Zarejestruj ponownie' : 'Zarejestruj kolejną umowę',
                onAcceptBtn: () => {
                    this.$store.dispatch('toggleModal', false)
                    this.$nextTick(() => {
                        this.$store.dispatch('openModal', {
                            componentPath: 'drs/operators/SelectOperator'
                        })
                    })
                }
            }

            this.$store.dispatch('openModal', {
                componentPath: 'global/ui/ModalSummary',
                args: { status, details, rejectBtn, acceptBtn }
            })
        },
        updateLocationContract(locId, payload) {
            this.$set(this.contractsData, locId, payload)
        },
        isContractValidFor(locId) {
            const contract = this.contractsData[locId]
            const loc = this.locations.find(({ id }) => id === +locId)
            if (!loc?.checked || !contract) return false

            const isRetailerToken = this.selectedOperator.code !== OPERATOR_CODES.POLKA || contract.retailerToken.trim()
            const enabledFractions = contract.fractions.filter(f => f.enabled)
            return (
                contract.collectionPointId &&
                !contract.invalidCollectionPointId &&
                isRetailerToken &&
                contract.startDate &&
                (contract.endDate || contract.indefinite) &&
                enabledFractions.length > 0 &&
                enabledFractions.every(f => +f.handlingFee > 0)
            )
        },

        toggleFraction({ val, fractionId }) {
            const contractFraction = this.contract.fractions.find(fr => fr.id === fractionId)
            this.$set(contractFraction, 'enabled', val)
        }
    }
}
</script>

<style lang="scss">
@import '@/styles/_custom.scss';
@import '~bootstrap/scss/mixins';

.card-body {
    .shop-accordion {
        border: 1px solid var(--color-dark);
        margin-bottom: 0.5rem !important;
    }
}

.operator-contract {
    &__dates {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 1rem;
    }

    &__date {
        width: 300px;
        margin-bottom: 0;
    }

    &__fractions {
        --gap: 1rem;
        display: flex;
        flex-wrap: wrap;
        gap: var(--gap);

    }

    &__fraction {
        width: calc(50% - var(--gap) / 2);
        padding: 1rem;
        border: 1px solid var(--color-gray-300);
        border-radius: 4px;
        cursor: pointer;
        user-select: none;

        &.active {
            border-color: var(--color-primary);
        }
    }

    &__fees {
        display: flex;
        flex-wrap: wrap;
        gap: 1rem;
    }

    &__fee {
        display: inline-flex;
        align-items: center;
        justify-content: space-between;
        gap: 1rem;
        flex: 1;

        h6 {
            margin-bottom: 0;
            font-weight: 500;
        }
    }

    @include media-breakpoint-down(sm) {
        &__fraction {
            width: 100%;
        }
    }
}
</style>